import React, { useState, useEffect } from "react";
import { DeleteButton, EditButton } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import { Space, Table, Tag, Card, Input } from "antd";
import { SearchOutlined, PlusOutlined, DollarOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/ui";

interface Currency {
    id: number;
    currencyCode: string;
    name: string;
    symbol: string;
    exchangeRate: number;
    isBaseCurrency: boolean;
    isActive: boolean;
    createdAt: string;
}

export const CurrencyList = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<Currency[]>([]);
    const navigate = useNavigate();

    const { data, isLoading } = useList({
        resource: "currency",
    });

    useEffect(() => {
        if (!data?.data) return;

        let currencies = data.data as Currency[];

        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            currencies = currencies.filter((currency: Currency) =>
                currency.currencyCode?.toLowerCase().includes(searchLower) ||
                currency.name?.toLowerCase().includes(searchLower)
            );
        }

        const sorted = [...currencies].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setFilteredItems(sorted);
    }, [data, searchText]);

    const columns = [
        {
            title: "Code",
            dataIndex: "currencyCode",
            key: "currencyCode",
            render: (value: string) => (
                <span className="font-medium text-text-primary">{value}</span>
            ),
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (value: string) => (
                <span className="text-text-secondary">{value || "-"}</span>
            ),
        },
        {
            title: "Symbol",
            dataIndex: "symbol",
            key: "symbol",
            render: (value: string) => (
                <span className="font-semibold text-primary">{value}</span>
            ),
        },
        {
            title: "Exchange Rate",
            dataIndex: "exchangeRate",
            key: "exchangeRate",
            render: (value: number) => (
                <span className="text-text-secondary">{value?.toFixed(4)}</span>
            ),
            responsive: ['md'] as any,
        },
        {
            title: "Base",
            dataIndex: "isBaseCurrency",
            key: "isBaseCurrency",
            render: (value: boolean) => (
                <Tag color={value ? "success" : "default"} className="status-tag">
                    {value ? "Yes" : "No"}
                </Tag>
            ),
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "status",
            render: (value: boolean) => (
                <Tag color={value ? "success" : "default"} className="status-tag">
                    {value ? "Active" : "Inactive"}
                </Tag>
            ),
        },
        {
            title: "Created",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            responsive: ['lg'] as any,
        },
        {
            title: "Actions",
            key: "actions",
            width: 100,
            render: (_: any, record: Currency) => (
                <Space size="small">
                    <EditButton hideText size="small" recordItemId={record.id} />
                    <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Currencies"
                icon={<DollarOutlined />}
                breadcrumbs={[{ title: "Currencies" }]}
                actions={[
                    {
                        label: "Create",
                        onClick: () => navigate("/currency/create"),
                        type: "primary",
                        icon: <PlusOutlined />,
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by currency code or name"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 800 }}
                    />
                </Card>
            </div>
        </div>
    );
};
